import { SpecialEvents, VideoEvents, trackEvent } from "@punchup/analytics";
import { logException, logMessage } from "@punchup/sentry";

import { Livestream } from "@punchup/data/legacy/livestreaming";
import { SpecialRow } from "@punchup/data/legacy/specials";
import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import MediaError from "video.js/dist/types/media-error";
import { VideoPlayerSurface } from "../types";
import type { PodcastPlaybackContextValue } from "./PodcastPlaybackContext";

const now = () => performance.now() / 1000;

const debugLogs = false;

const LARGE_DELTA_THRESHOLD = 2; // 2 seconds is on average 8x larger than what we expect

// NOTE: We don't really care about paused state since timeUpdated is called only when playing
enum VideoLoggerState {
  NOT_STARTED = "NOT_STARTED",
  PLAYING = "PLAYING",
  SEEKING = "SEEKING",
  ENDED = "ENDED",
}

class VideoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VideoError";
  }
}

export class VideoLogger {
  _sid: number;
  _lastPlayhead = 0;
  _watchtime = 0;
  _lastDelta = 0;
  _start: number | null = null;
  _state: VideoLoggerState = VideoLoggerState.NOT_STARTED;
  _isMuted = false;
  _skipNextSeekEvent = false; // used when skipping forward or backward
  _subtitlesActive = false;
  _playbackRate = 1;
  _isFullscreen = false;
  _duration: number | null = null;

  _didLog3s = false;
  _didLog10s = false;
  _didLog30s = false;

  _podcastContext: PodcastPlaybackContextValue | null = null;

  setPodcastContext(value: PodcastPlaybackContextValue): void {
    this._podcastContext = value;
  }

  clearPodcastContext(): void {
    this._podcastContext = null;
  }

  _podcastProps(): Record<string, unknown> {
    if (!this._podcastContext) return {};
    const ctx = this._podcastContext;
    const out: Record<string, unknown> = {
      content_type: ctx.contentType,
      podcast_id: ctx.podcastId,
      podcast_slug: ctx.podcastSlug,
    };
    if (ctx.ctaSource) out.cta_source = ctx.ctaSource;
    if (ctx.pageSection) out.page_section = ctx.pageSection;
    return out;
  }

  constructor(
    public special: SpecialRow,
    public isPreview: boolean,
    public surface: VideoPlayerSurface,
    public livestream: Livestream | null = null,
    public supabase: ReturnType<
      typeof createClientComponentClient<Database>
    > | null = null
  ) {
    this._sid = Math.floor(Math.random() * 1000000);
    this._log("New Logger");
  }

  reset() {
    this._sid = Math.floor(Math.random() * 1000000);
    this._lastPlayhead = 0;
    this._watchtime = 0;
    this._lastDelta = 0;
    this._start = null;
    this._state = VideoLoggerState.NOT_STARTED;
    this._duration = null;
  }

  setDuration(duration: number): void {
    this._duration = duration > 0 ? duration : null;
  }

  setSpecial(special: SpecialRow, isPreview: boolean) {
    this.special = special;
    this.isPreview = isPreview;
    this.reset();
  }

  /**
   * Start the video session.
   * Can be called multiple times, so it's safe to call it every onPlay event.
   */
  start(startTime: number): void {
    this._logEvent(SpecialEvents.SPECIAL_CLICKED_PLAY);

    if (this._state === VideoLoggerState.ENDED) {
      // Allow creating a new session if starting again
      this.reset();
    }
    if (this._state !== VideoLoggerState.NOT_STARTED) {
      // already started
      return;
    }
    this._state = VideoLoggerState.PLAYING;
    this._start = now();
    this._lastPlayhead = startTime;
    this._logEvent(VideoEvents.VIDEO_SESSION_STARTED);
  }

  pause() {
    this._logEvent(SpecialEvents.SPECIAL_CLICKED_PAUSE);
  }

  mutedChanged(isMuted: boolean) {
    this._isMuted = isMuted;
  }

  /**
   * Ends the video session.
   * Logs the watchtime event.
   */
  end(reason?: string): void {
    this._log("end; current: " + this._state);
    if (this._state !== VideoLoggerState.PLAYING || !this._start) {
      // already ended or never started
      return;
    }
    this._state = VideoLoggerState.ENDED;

    const wallclocktime = now() - this._start;
    if (wallclocktime < this._watchtime / 3) {
      /**
       * This is to help us diagnose why we see some very high watchtime values while wallclock time is low.
       * Example: Getting 5 minutes of watchtime in 3 seconds is suspect and likely indicates a bug.
       * Since this is likely a bug, log that this occurred and ignore the session end event so we don't polute
       * our watchtime metrics.
       */
      logMessage(
        "Unreasonably high watchtime; ignore session end event",
        "warning"
      );
    } else if (this._watchtime < 0) {
      logMessage("Negative watchtime: ignore session end event", "warning");
    } else {
      this._logEvent(
        VideoEvents.VIDEO_SESSION_ENDED,
        {
          watchtime: this._watchtime,
          watched_pct:
            this._duration != null ? this._watchtime / this._duration : null,
          reason,
        },
        true
      );
    }
  }

  /**
   * Updates current playhead and logs Viewed 3/10/30s events.
   */
  timeUpdated(time: number): void {
    if (this._state !== VideoLoggerState.PLAYING) {
      return;
    }
    const delta = time - this._lastPlayhead;
    if (!this._isMuted) {
      // Don't track muted watchtime (mostly for autoplay muted videos)
      this._watchtime += delta;
    }
    this._lastDelta = delta;
    this._lastPlayhead = time;

    this._debugLogStats();
    this._onTimeUpdated();
  }

  playbackRateChanged(rate: number | undefined): void {
    this._playbackRate = rate || 1;
    this._logEvent(VideoEvents.VIDEO_PLAYBACK_RATE_CHANGED);
  }

  textTrackChanged(track: TextTrack | null): void {
    if ((track != null) === this._subtitlesActive) {
      return;
    }
    this._subtitlesActive = track != null;
    this._logEvent(VideoEvents.VIDEO_SUBTITLES_CHANGED, {
      label: track?.label,
      language: track?.language,
    });
  }

  volumeChanged(): void {
    this._logEvent(VideoEvents.VIDEO_VOLUME_PANEL_OPENED);
  }

  fullscreenChanged(isFullscreen: boolean | undefined): void {
    this._isFullscreen = isFullscreen || false;
    this._logEvent(VideoEvents.VIDEO_FULLSCREEN_CHANGED);
  }

  skipForward(): void {
    this._logEvent(VideoEvents.VIDEO_SKIP_FORWARD);
    this._skipNextSeekEvent = true;
  }

  skipBackward(): void {
    this._logEvent(VideoEvents.VIDEO_SKIP_BACKWARD);
    this._skipNextSeekEvent = true;
  }

  /**
   * Informs the logger we started seeking so we can ignore future timeUpdated events.
   * This allows us to not count watchtime of the seeking.
   */
  startSeek(): void {
    if (this._state !== VideoLoggerState.PLAYING) {
      return;
    }
    this._state = VideoLoggerState.SEEKING;
    // The seek event doesn't always come before the timeUpdated event, so sometimes the
    // watchtime is incremented with the "seek" time, which artifically inflates watchtime.
    // This adjusts for that.
    if (Math.abs(this._lastDelta) > LARGE_DELTA_THRESHOLD) {
      this._watchtime -= this._lastDelta;
    }
  }

  /**
   * Informs the logger we ended seeking so we can start logging timeUpdated events again.
   */
  endSeeking(time: number): void {
    if (this._skipNextSeekEvent) {
      // If skip forward or backward was called, we don't want to log the seeking event
      this._skipNextSeekEvent = false;
    } else if (this._state !== VideoLoggerState.NOT_STARTED) {
      this._logEvent(SpecialEvents.SPECIAL_SEEKING);
    }

    if (
      this._state !== VideoLoggerState.SEEKING &&
      this._state !== VideoLoggerState.NOT_STARTED
    ) {
      logMessage(
        "Illegal transition: endSeeking called while state is " + this._state,
        "warning"
      );
      return;
    }
    // Only transition to PLAYING when we were actually mid-SEEK during an
    // active session. When state is NOT_STARTED (e.g. the initial currentTime
    // seek that restores lastPlaybackLocation before autoplay), leave it alone
    // — otherwise start() will see state=PLAYING, skip VIDEO_SESSION_STARTED,
    // and never set _start, which also breaks VIDEO_SESSION_ENDED and watchtime.
    if (this._state === VideoLoggerState.SEEKING) {
      this._state = VideoLoggerState.PLAYING;
    }
    this._lastPlayhead = time;
    this._debugLogStats();
  }

  stalled(): void {
    // TODO: Capture stalled time
    this._logEvent(VideoEvents.VIDEO_STALLED);
    logMessage("Video Stalled", "info");
  }

  error(error?: MediaError | null | undefined): void {
    // NOTE: I can't make the CloudFlare player produce an error, so I don't know how to capture the actual error
    this._logEvent(VideoEvents.VIDEO_ERROR, { error: error?.message });
    logException(new VideoError(error?.message || "Unknown"), {
      tags: {
        livestream: !!this.livestream?.id,
        preview: this.isPreview,
        specialId: this.special.id,
      },
    });
  }

  _onTimeUpdated(): void {
    // Ignore large jumps in playhead. If the playhead jumps by more than 2 seconds, ignore the event as it's likely a seek event
    if (this._lastDelta > LARGE_DELTA_THRESHOLD) {
      this._log("Ignore onTimeUpdated due to large lastDelta");
      return;
    }
    if (this._watchtime > 3 && !this._didLog3s) {
      this._didLog3s = true;
      this._logEvent(VideoEvents.VIDEO_VIEWED_3S);
    }
    if (this._watchtime > 10 && !this._didLog10s) {
      this._didLog10s = true;
      this._logEvent(VideoEvents.VIDEO_VIEWED_10S);
    }
    if (this._watchtime > 30 && !this._didLog30s) {
      this._didLog30s = true;
      this._logEvent(VideoEvents.VIDEO_VIEWED_30S);
    }
  }

  _logEvent(event: string, data?: any, sendImmediately?: boolean) {
    this._log(event);

    trackEvent(
      event,
      {
        // Podcast context goes FIRST so explicit common props or caller-passed
        // `data` always win on key collision. Today there's no overlap, but
        // ordering this way keeps future additions safe (e.g. if a new common
        // prop later uses content_type / podcast_id, it shadows the
        // context-derived value rather than silently being overridden by it).
        ...this._podcastProps(),
        ...data,
        location: this._lastPlayhead,
        isPreview: this.isPreview,
        special_id: this.special.id,
        t: this._start && now() - this._start,
        sid: this._sid,
        comedian_id: this.special.comedian_id,
        special_name: this.special.title,
        livestream_id: this.livestream?.id,
        surface: this.surface,
        playback_rate: this._playbackRate,
        subtitles_active: this._subtitlesActive,
        is_fullscreen: this._isFullscreen,
      },
      sendImmediately
        ? { send_immediately: true, transport: "sendBeacon" }
        : undefined
    );
  }

  _debugLogStats() {
    if (!debugLogs) return;

    this._log(
      `watchtime: ${this._watchtime.toFixed(2)}, ` +
        `last: ${this._lastPlayhead.toFixed(2)}, ` +
        `delta: ${this._lastDelta.toFixed(2)}`
    );
  }

  _log(s: string) {
    if (!debugLogs) return;
    console.log(
      `[${this._sid}@${(this._start ? now() - this._start : -1)?.toFixed(
        2
      )}] ` + `${s} @ ${this._lastPlayhead}`
    );
  }
}
